import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiGet, getToken } from '@/utils/api';
import { Trash2, Shield, Users, ChevronDown, ChevronUp, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ title, value, subtitle, icon, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function UserRow({ user, index, isSelected, onToggleSelect }) {
  const lastLogin = user.last_login 
    ? new Date(user.last_login).toLocaleDateString() 
    : 'Never';
  const joinDate = user.created_at 
    ? new Date(user.created_at).toLocaleDateString() 
    : 'Unknown';

  const getTierBadge = (tier) => {
    const badges = {
      'Free': 'bg-gray-100 text-gray-600',
      'Premium': 'bg-emerald-100 text-emerald-700',
      'Household': 'bg-purple-100 text-purple-700',
      'Lifetime': 'bg-amber-100 text-amber-700'
    };
    return badges[tier] || badges['Free'];
  };

  return (
    <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isSelected ? 'bg-red-50' : ''}`}>
      {/* Checkbox */}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(user.user_id)}
          className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
          data-testid={`select-user-${user.user_id}`}
        />
      </td>
      {/* User Info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
              {user.first_name?.charAt(0) || user.email?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{user.first_name || 'No name'}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </div>
      </td>
      {/* Date Joined */}
      <td className="px-4 py-3 text-sm text-gray-600">{joinDate}</td>
      {/* Last Login */}
      <td className="px-4 py-3 text-sm text-gray-600">{lastLogin}</td>
      {/* Subscription Tier */}
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTierBadge(user.subscription_tier)}`}>
          {user.subscription_tier}
        </span>
      </td>
      {/* Plaid Connected */}
      <td className="px-4 py-3 text-center">
        {user.plaid_connected ? (
          <span className="text-green-600 font-medium">Yes</span>
        ) : (
          <span className="text-gray-400">No</span>
        )}
      </td>
      {/* Streak */}
      <td className="px-4 py-3 text-center">
        {user.streak_count > 0 ? (
          <span className="text-lg" title={`${user.streak_count} day streak`}>
            🔥 {user.streak_count}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
    </tr>
  );
}

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    loadAdminData();
  }, [navigate]);

  const loadAdminData = async () => {
    const token = getToken();
    if (!token) {
      navigate('/app/login');
      return;
    }

    try {
      const statsRes = await fetch(`${API}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (statsRes.status === 403) {
        setError('Access denied. Admin only.');
        setLoading(false);
        return;
      }

      if (!statsRes.ok) throw new Error('Failed to load admin data');

      const statsData = await statsRes.json();
      setStats(statsData.stats);
      setUsers(statsData.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.user_id)));
    }
  };

  const handleDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    
    setDeleting(true);
    const token = getToken();
    
    try {
      const response = await fetch(`${API}/admin/users/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ user_ids: Array.from(selectedUsers) })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete users');
      }
      
      const result = await response.json();
      
      // Refresh data
      setSelectedUsers(new Set());
      setShowDeleteConfirm(false);
      await loadAdminData();
      
      alert(`Successfully deleted ${result.deleted_count} user(s)`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    if (filter === 'active') {
      return user.has_activity;
    } else if (filter === 'inactive') {
      return !user.has_activity;
    } else if (filter === 'premium') {
      return user.subscription_tier !== 'Free';
    }
    return true;
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    
    if (sortField === 'created_at' || sortField === 'last_login') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }
    
    if (sortDir === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const SortHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Go to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-emerald-100">Zero-Knowledge Privacy • User Metrics Only</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/internal-distribution"
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg font-medium transition-colors"
                data-testid="manage-lifetime-invites-btn"
              >
                <Crown className="w-5 h-5" />
                Manage Lifetime Invites
              </Link>
              <button
                onClick={() => navigate('/app')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
              >
                ← Back to App
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Privacy Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-blue-900 font-medium">Zero-Knowledge Admin Access</p>
              <p className="text-blue-700 text-sm">
                For user privacy, you cannot see individual financial data (income, bills, goals, debts). 
                Only aggregate totals and non-sensitive user metadata are displayed.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid - Aggregate Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon="👥"
            color="text-blue-600"
          />
          <StatCard
            title="Active Users"
            value={stats?.active_users || 0}
            subtitle="With any data"
            icon="✅"
            color="text-green-600"
          />
          <StatCard
            title="Users with Data"
            value={stats?.users_with_data || 0}
            subtitle="Tested the app"
            icon="📊"
            color="text-purple-600"
          />
          <StatCard
            title="New This Week"
            value={stats?.new_this_week || 0}
            icon="🆕"
            color="text-orange-600"
          />
        </div>

        {/* Aggregate Financial Stats (Totals only, not per-user) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard
            title="Total Bills"
            value={stats?.total_bills || 0}
            subtitle="All users combined"
            icon="📝"
            color="text-indigo-600"
          />
          <StatCard
            title="Total Income"
            value={stats?.total_income || 0}
            subtitle="All users combined"
            icon="💰"
            color="text-emerald-600"
          />
          <StatCard
            title="Total Goals"
            value={stats?.total_goals || 0}
            subtitle="All users combined"
            icon="🎯"
            color="text-pink-600"
          />
          <StatCard
            title="Total Debts"
            value={stats?.total_debts || 0}
            subtitle="All users combined"
            icon="💳"
            color="text-red-600"
          />
          <StatCard
            title="Active Streaks"
            value={stats?.active_streaks || 0}
            icon="🔥"
            color="text-orange-600"
          />
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Activity
            </h2>
            <div className="flex flex-wrap gap-2">
              {['all', 'active', 'inactive', 'premium'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filter === f
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Delete Selected Button */}
          {selectedUsers.size > 0 && (
            <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center justify-between">
              <span className="text-red-700 font-medium">
                {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                data-testid="delete-selected-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                    />
                  </th>
                  <SortHeader field="email">User</SortHeader>
                  <SortHeader field="created_at">Date Joined</SortHeader>
                  <SortHeader field="last_login">Last Login</SortHeader>
                  <SortHeader field="subscription_tier">Tier</SortHeader>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Plaid</th>
                  <SortHeader field="streak_count">Streak</SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedUsers.map((user, index) => (
                  <UserRow 
                    key={user.user_id} 
                    user={user} 
                    index={index}
                    isSelected={selectedUsers.has(user.user_id)}
                    onToggleSelect={handleToggleSelect}
                  />
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No users found matching this filter.
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Users?</h3>
              <p className="text-gray-600 mb-6">
                This will permanently delete <strong>{selectedUsers.size}</strong> user{selectedUsers.size > 1 ? 's' : ''} and all their data. 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUsers}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
